function _inheritsLoose(t, o) { t.prototype = Object.create(o.prototype), t.prototype.constructor = t, _setPrototypeOf(t, o); }
function _setPrototypeOf(t, e) { return _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function (t, e) { return t.__proto__ = e, t; }, _setPrototypeOf(t, e); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import Vector from 'ol/layer/Vector';
import Source from 'ol/source/Source';
import Style from 'ol/style/Style';
import React from 'react';
import PropTypes from 'prop-types';
import { ensureMap } from '../../hocs/ensureMap';
export var LayerContext = React.createContext({
  layer: null
});

/**
 * Wraps and manages ol/layer/Vector. Adds layer to map on first mount, and
 * deals with visibility according to component lifecycle.
 *
 * Accepts a source like ol/source/Vector or ol/source/Cluster. See
 * ClusterSource and VectorSource for implementations.
 *
 * Accepts a style like ol/style/Style or a style function returning one or
 * more ol/style/Style. See defaultStyle and other utilities for existing
 * implementations, or provide a custom one.
 *
 * <strong>Not typically used directly. See VectorSource and ClusterSource for ready
 * to use components.</strong>
 *
 * @typedef {(function|Style|[Style])} StyleLike
 */
export var VectorLayer = /*#__PURE__*/function (_React$PureComponent) {
  function VectorLayer() {
    return _React$PureComponent.apply(this, arguments) || this;
  }
  _inheritsLoose(VectorLayer, _React$PureComponent);
  var _proto = VectorLayer.prototype;
  _proto.componentDidMount = function componentDidMount() {
    this.checkProps(this.props);
  };
  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    this.checkProps(this.props, prevProps);
  };
  _proto.checkProps = function checkProps(props, prevProps) {
    var map = props.map,
      layerName = props.layerName,
      source = props.source,
      styleFn = props.styleFn,
      style = props.style,
      minZoom = props.minZoom,
      maxZoom = props.maxZoom,
      selectable = props.selectable,
      getResolution = props.getResolution,
      zIndex = props.zIndex;
    if (prevProps && prevProps.layerName !== layerName && map._newVectorLayers && map._newVectorLayers[prevProps.layerName]) {
      map._newVectorLayers[prevProps.layerName].layer.setVisible(false);
    }
    var minResolution = maxZoom && getResolution(maxZoom);
    var maxResolution = minZoom && getResolution(minZoom);
    if (map._newVectorLayers && map._newVectorLayers[layerName]) {
      var layer = map._newVectorLayers[layerName].layer;
      if (prevProps && prevProps.style !== style) {
        layer.setStyle(style);
      }
      if (!layer.getVisible()) layer.setVisible(true);
    } else {
      map._newVectorLayers = map._newVectorLayers || {};

      // Create layer, add to map
      var _layer = new Vector({
        source: source,
        minResolution: minResolution,
        maxResolution: maxResolution,
        selectable: selectable,
        style: styleFn ? styleFn() : style,
        zIndex: zIndex
      });
      map._newVectorLayers[layerName] = {
        layer: _layer
      };
      this.mountLayer();
    }
  };
  _proto.mountLayer = function mountLayer() {
    var _this$props = this.props,
      map = _this$props.map,
      layerName = _this$props.layerName;
    map.addLayer(map._newVectorLayers[layerName].layer);
    this.setState({
      mounted: true
    });
  };
  _proto.componentWillUnmount = function componentWillUnmount() {
    var _this$props2 = this.props,
      map = _this$props2.map,
      layerName = _this$props2.layerName;
    map._newVectorLayers[layerName].layer.setVisible(false);
  };
  _proto.render = function render() {
    if (this.props.children) {
      var _this$props3 = this.props,
        map = _this$props3.map,
        layerName = _this$props3.layerName;
      if (map && map._newVectorLayers && map._newVectorLayers[layerName]) {
        var layer = map._newVectorLayers[layerName].layer;
        return /*#__PURE__*/React.createElement(LayerContext.Provider, {
          value: {
            layer: layer,
            map: map
          }
        }, this.props.children);
      }
    }
    return /*#__PURE__*/React.createElement("span", null);
  };
  return VectorLayer;
}(React.PureComponent);
_defineProperty(VectorLayer, "defaultProps", {
  selectable: true
});
VectorLayer.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Name presented in layer switch/groups.
   */
  name: PropTypes.string,
  /**
   * Internal name of the layer, must be unique per map.
   */
  layerName: PropTypes.string.isRequired,
  /**
   * Source for the vector layer. Passed directly to the layer. Cannot be
   * changed after layer is created. See VectorSource and ClusterSource
   * for implementations.
   */
  source: PropTypes.instanceOf(Source),
  /**
   * Style or style function for vector layer. Passed directly to the layer.
   *
   * Use styleFn to create a customized style on demand (once).
   */
  style: PropTypes.oneOfType([PropTypes.instanceOf(Style), PropTypes.arrayOf(Style), PropTypes.func]),
  /**
   * Called when layer is created.
   * @returns {StyleLike}
   */
  styleFn: PropTypes.func,
  /**
   * If set, do not allow lower zoom level than provided value.
   */
  minZoom: PropTypes.number,
  /**
   * If set, do not allow higher zoom level than provided value.
   */
  maxZoom: PropTypes.number,
  /**
   * Set to false to avoid map click events on features in this layer.
   */
  selectable: PropTypes.bool,
  /**
   * The z-index for layer rendering.
   * At rendering time, the layers will be ordered, first by Z-index and then by position.
   * When undefined, a zIndex of 0 is assumed for layers that are added to the map's layers collection,
   * or Infinity when the layer's setMap() method was used.
   */
  zIndex: PropTypes.number,
  /**
   * Provided by ensureMap HOC.
   * @ignore
   */
  map: PropTypes.object,
  /**
   * Provided by ensureMap HOC.
   * @ignore
   */
  getResolution: PropTypes.func
} : {};
export default ensureMap(VectorLayer);